import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ArrowLeft } from 'lucide-react';
import { BreakDuration } from '../../../types/state/timecards';
import { toast } from 'react-toastify';

// Constants for break duration limits
const MIN_BREAK_DURATION = 1; // 1 minute minimum
// const MAX_BREAK_DURATION = 480; // 8 hours (480 minutes) maximum
const MAX_BREAK_DURATION = 1440; // 8 hours (480 minutes) maximum

interface BreakManagerOverlayProps {
  isOpen: boolean;
  breaks: BreakDuration[];
  editIndex?: number | null;
  onSave: (updatedBreaks: BreakDuration[]) => void;
  onClose: () => void;
}

const BreakManagerOverlay: React.FC<BreakManagerOverlayProps> = ({
  isOpen,
  breaks: initialBreaks,
  editIndex: initialEditIndex,
  onSave,
  onClose
}) => {
  // Always show the form directly - no intermediate page
  const [newBreak, setNewBreak] = useState<BreakDuration>({
    type: 'unpaid',
    duration_minutes: 30
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(initialEditIndex || null);
  
  // Use string-based state for date/time inputs (like AddTimesheetEntry)
  const [startDateTime, setStartDateTime] = useState<string>("");
  const [endDateTime, setEndDateTime] = useState<string>("");
  
  // Toggle between duration input modes - true for start/end time, false for direct duration input
  const [isTimeInputMode, setIsTimeInputMode] = useState<boolean>(true);

  // Reset form when open state or edit index changes
  useEffect(() => {
    if (isOpen) {
      // Initialize with defaults or edited break data
      if (typeof initialEditIndex === 'number' && initialBreaks && initialBreaks[initialEditIndex]) {
        const existingBreak = initialBreaks[initialEditIndex];
        
        // If the break has start/end times, initialize in time mode
        if (existingBreak.start_datetime && existingBreak.end_datetime) {
          console.log('existing break dtm before format:', existingBreak);
          setIsTimeInputMode(false); // false means time mode in our toggle
          
          // Parse the dates maintaining local time
          const formatForInput = (dateStr: string) => {
            const [datePart, timePart] = dateStr.split(' ');
            return `${datePart}T${timePart.slice(0, 5)}`; // Convert "2025-06-18 07:52:00" to "2025-06-18T07:52"
          };
          
          const formattedStart = formatForInput(existingBreak.start_datetime);
          const formattedEnd = formatForInput(existingBreak.end_datetime);
          
          console.log('Formatted dates:', { formattedStart, formattedEnd });
          
          setStartDateTime(formattedStart);
          setEndDateTime(formattedEnd);
          setNewBreak({
            type: existingBreak.type || 'unpaid',
            start_datetime: formattedStart,
            end_datetime: formattedEnd
          });
        } else {
          // Otherwise initialize in duration/hours mode
          setIsTimeInputMode(true);
          setNewBreak({  
            type: existingBreak.type || 'unpaid',
            duration_minutes: existingBreak.duration_minutes || 30
          });
        }
        setEditingIndex(initialEditIndex);
      } else {
        // Default to duration mode for new breaks
        setIsTimeInputMode(false);
        setNewBreak({
          type: 'unpaid',
          duration_minutes: 30
        });
        setEditingIndex(null);
        
        // Set current date/time as default values only for new breaks
        const now = new Date();
        const later = new Date(now.getTime() + 30 * 60000);
        setStartDateTime(now.toISOString().slice(0, 16));
        setEndDateTime(later.toISOString().slice(0, 16));
      }
    }
  }, [isOpen, initialBreaks, initialEditIndex]);

  const validateBreak = (breakData: BreakDuration): { valid: boolean; message?: string } => {
    if (!isTimeInputMode) {
      // For time-based mode
      if (!breakData.start_datetime || !breakData.end_datetime) {
        return { valid: false, message: 'Both start and end times are required' };
      }
      
      if (new Date(breakData.end_datetime) <= new Date(breakData.start_datetime)) {
        return { valid: false, message: 'End time must be after start time' };
      }
      
      // Validate that the time difference isn't too long
      const durationMs = new Date(breakData.end_datetime).getTime() - new Date(breakData.start_datetime).getTime();
      const durationMinutes = Math.round(durationMs / 60000);
      if (durationMinutes > MAX_BREAK_DURATION) {
        return { valid: false, message: `Break duration cannot exceed 8 hours (${MAX_BREAK_DURATION} minutes)` };
      }
    } else {
      // For duration/hours mode
      if (typeof breakData.duration_minutes !== 'number' || breakData.duration_minutes <= 0) {
        return { valid: false, message: 'Break duration must be greater than 0 minutes' };
      }
      
      if (breakData.duration_minutes > MAX_BREAK_DURATION) {
        return { valid: false, message: `Break duration cannot exceed 8 hours (${MAX_BREAK_DURATION} minutes)` };
      }
    }
    
    return { valid: true };
  };
  
  // Format a date for display in the form
  const formatDateDisplay = (date: Date): string => {
    return date.toLocaleString('en-US', {
      day: 'numeric', 
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Function to handle duration changes
  const handleDurationChange = (value: string) => {
    const minutes = value === '' ? undefined : parseInt(value);
    
    // Allow empty string or any number - validation will happen on save
    setNewBreak({
      ...newBreak,
      duration_minutes: minutes
    });
    
    // Only update end time if we have a valid number
    if (startDateTime && typeof minutes === 'number') {
      const startMs = new Date(startDateTime).getTime();
      const newEndTime = new Date(startMs + minutes * 60000);
      setEndDateTime(newEndTime.toISOString().slice(0, 16));
    }
  };

  const handleAddBreak = () => {
    // Create the appropriate break object based on mode
    const breakToSave: BreakDuration = !isTimeInputMode 
      ? {
          type: newBreak.type,
          start_datetime: startDateTime,
          end_datetime: endDateTime,
          // Clear any duration_minutes when in time mode
          duration_minutes: undefined
        }
      : {
          type: newBreak.type,
          duration_minutes: newBreak.duration_minutes,
          // Clear any time fields when in duration mode
          start_datetime: undefined,
          end_datetime: undefined
        };
    
    const validation = validateBreak(breakToSave);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    const updatedBreaks = [...initialBreaks];
    
    if (editingIndex !== null) {
      updatedBreaks[editingIndex] = breakToSave;
    } else {
      updatedBreaks.push(breakToSave);
    }

    onSave(updatedBreaks);
  };

  const formatBreakTime = (breakData: BreakDuration): string => {
    if (breakData.duration_minutes !== undefined) {
      const hours = Math.floor(breakData.duration_minutes / 60);
      const minutes = breakData.duration_minutes % 60;
      
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else {
        return `${minutes}m`;
      }
    }
    return '';
  };
  
  // Handlers for datetime inputs
  const handleStartDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDateTime = e.target.value;
    console.log('New start datetime:', newStartDateTime);
    setStartDateTime(newStartDateTime);
    setNewBreak(prev => ({
      ...prev,
      start_datetime: newStartDateTime
    }));
  };
  
  const handleEndDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDateTime = e.target.value;
    console.log('New end datetime:', newEndDateTime);
    setEndDateTime(newEndDateTime);
    setNewBreak(prev => ({
      ...prev,
      end_datetime: newEndDateTime
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white">
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <button onClick={onClose} className="text-blue-600 flex items-center">
          <ArrowLeft size={18} />
          <span className="ml-1">Back</span>
        </button>
        <h1 className="text-md font-medium">{editingIndex !== null ? 'Edit Break' : 'Add Break'}</h1>
        <div></div> {/* Empty div for flex alignment */}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* Toggle for input mode - using same labels as AddTimesheetEntry */}
        <div className="mb-4 flex justify-between items-center">
          <label className="text-sm font-medium">Input Mode</label>
          <div className="flex items-center">
            <span className={`mr-2 text-sm ${!isTimeInputMode ? 'font-medium text-blue-600' : 'text-gray-500'}`}>Time</span>
            <button
              type="button"
              role="switch"
              aria-checked={isTimeInputMode}
              onClick={() => {
                // Toggle input mode
                setIsTimeInputMode(!isTimeInputMode);
                
                // When switching to duration mode, ensure we have a duration value
                if (!isTimeInputMode === true && !newBreak.duration_minutes) {
                  setNewBreak({
                    ...newBreak,
                    duration_minutes: 30 // Default duration
                  });
                }
              }}
              className={`relative inline-flex items-center h-5 rounded-full w-10 transition-colors ${
                isTimeInputMode ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                  isTimeInputMode ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
            <span className={`ml-2 text-sm ${isTimeInputMode ? 'font-medium text-blue-600' : 'text-gray-500'}`}>Duration</span>
          </div>
        </div>

        {isTimeInputMode ? (
          <>
            {/* Hours-based input mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Duration (minutes)</label>
              <input 
                type="number"
                min={MIN_BREAK_DURATION}
                max={MAX_BREAK_DURATION}
                value={newBreak.duration_minutes || ''}
                onKeyDown={(e) => {
                  // Allow only numbers and special keys (backspace, delete, arrows, etc.)
                  if (!/^\d+$/.test(e.key) && 
                      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    handleDurationChange('');
                  } else {
                    const num = parseInt(value);
                    if (num <= MAX_BREAK_DURATION) { // Only update if within valid range
                      handleDurationChange(value);
                    }
                  }
                }}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Duration quick selection - only shown in hours mode */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Quick Select</label>
              <div className="grid grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => handleDurationChange("15")} 
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 15 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  15 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationChange("30")}
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 30 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  30 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationChange("45")}
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 45 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  45 min
                </button>
                <button
                  type="button"
                  onClick={() => handleDurationChange("60")}
                  className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 60 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
                >
                  60 min
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Time-based input mode - with editable datetime inputs */}
            <div className="mb-4">
              <label className="block text-md font-medium mb-1">Start Time</label>
              <input
                type="datetime-local"
                value={startDateTime}
                onChange={handleStartDateTimeChange}
                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                placeholder="Start Date & Time"
              />
            </div>

            <div className="mb-4">
              <label className="block text-md font-medium mb-1">End Time</label>
              <input
                type="datetime-local"
                value={endDateTime}
                min={startDateTime}
                onChange={handleEndDateTimeChange}
                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                placeholder="End Date & Time"
              />
            </div>

            {/* <div className="mb-4">
              <div className="flex justify-between items-center">
                <label className="block text-md font-medium">Duration</label>
                <div className="text-md font-medium">
                  {startDateTime && endDateTime ? (
                    <>
                      {Math.max(MIN_BREAK_DURATION, Math.min(MAX_BREAK_DURATION, Math.round((new Date(endDateTime).getTime() - new Date(startDateTime).getTime()) / 60000)))} min
                      <span className="text-xs text-gray-500 ml-1">(calculated)</span>
                    </>
                  ) : (
                    <>Set start and end time</>
                  )}
                </div>
              </div>
            </div> */}
          </>
        )}

        <div className="flex justify-between items-center mb-6">
          <label className="text-sm font-medium">Paid Break</label>
          <button
            type="button"
            role="switch"
            aria-checked={newBreak.type === 'paid'}
            onClick={() => setNewBreak({
              ...newBreak,
              type: newBreak.type === 'paid' ? 'unpaid' : 'paid'
            })}
            className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
              newBreak.type === 'paid' ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                newBreak.type === 'paid' ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      <div className="p-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onClose}
            className="py-2 bg-gray-400 text-white rounded-md font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddBreak}
            className="py-2 bg-blue-600 text-white rounded-md font-medium text-sm"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreakManagerOverlay;
